import { ActionIcon, Avatar, Badge, Tooltip } from '@mantine/core';
import { IconMessageCircle, IconRepeat } from '@tabler/icons-react';
import classnames from 'classnames';
import dayjs from 'dayjs';
import { DataTable } from 'mantine-datatable';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { Props } from 'src/Components/Expenses/ExpenseList/ExpenseList.types';
import { ShareAvatar } from 'src/Components/Expenses/ShareAvatar';
import useIsMobile from 'src/Hooks/Shared/useIsMobile';
import { formatEuros } from 'src/Utils/formatEuros';

import styles from './ExpenseList.module.scss';

const ExpenseList = ({
  expenses,
  loading,
  paidByLabel,
  onRowClick,
  hideContactBadge,
  getPaidByLine,
  getStatus,
}: Props) => {
  const isMobile = useIsMobile();
  const { t } = useTranslation();
  const navigate = useNavigate();

  return (
    <DataTable
      withRowBorders={false}
      style={{ width: '100%', minHeight: '10rem' }}
      records={expenses}
      rowStyle={() => ({ height: 58 })}
      noRecordsText={t('expenses.empty', 'Aucune opération')}
      fetching={loading}
      onRowClick={({ record }) => {
        if (onRowClick) {
          onRowClick(record);
        } else {
          navigate(record.id);
        }
      }}
      columns={[
        {
          accessor: 'title',
          title: '',
          render: expense => {
            const {
              title,
              paidBy,
              paidByBankAccount,
              paidByContact,
              note,
              isRecurring,
              recurrenceSeriesId,
            } = expense;
            const paidByLine = getPaidByLine?.(expense);
            const status = getStatus?.(expense);

            return (
              <div className={styles.column}>
                <div className={styles.titleLine}>
                  <div className={styles.title}>{title}</div>
                  {(isRecurring || recurrenceSeriesId) && (
                    <Tooltip label={t('expenses.list.recurring', 'Dépense récurrente')}>
                      <IconRepeat size={14} className={styles.recurringIcon} />
                    </Tooltip>
                  )}
                  {paidByContact && !hideContactBadge && (
                    <Badge className={styles.contactBadge}>
                      {t('expenses.list.contactBadge', 'Contact : {{name}}', {
                        name: paidByContact.displayName,
                      })}
                    </Badge>
                  )}
                  {status && (
                    <Badge
                      className={styles.statusBadge}
                      color={status.color}
                      variant="light"
                      size="sm"
                    >
                      {status.label}
                    </Badge>
                  )}
                  {note && (
                    <Tooltip
                      label={note}
                      multiline
                      w={260}
                      withArrow
                      events={{ hover: true, focus: true, touch: true }}
                    >
                      <ActionIcon
                        variant="transparent"
                        size="sm"
                        onClick={e => e.stopPropagation()}
                        aria-label={t('expenses.list.hasNote', "Détails de l'opération")}
                      >
                        <IconMessageCircle size={16} className={styles.noteIcon} />
                      </ActionIcon>
                    </Tooltip>
                  )}
                </div>
                <div className={styles.paidByLine}>
                  {/* The date is what lets a co-owner recognise the operation: the balance
                      detail lists several lines that share the same title. */}
                  <span className={styles.date}>{dayjs(expense.date).format('DD/MM/YYYY')}</span>
                  {paidByLine ? (
                    <span className={styles.paidByName}>{paidByLine}</span>
                  ) : (
                    <>
                      <span className={styles.paidBy}>{paidByLabel}</span>
                      <span className={styles.paidByName}>
                        {paidByContact
                          ? paidByContact.displayName
                          : paidBy
                            ? [paidBy?.firstName, paidBy?.lastName].join(' ')
                            : paidByBankAccount?.name}
                      </span>
                    </>
                  )}
                </div>
              </div>
            );
          },
        },
        {
          accessor: 'type',
          title: '',
          hidden: isMobile,
          render: ({ type }) =>
            type ? <Badge className={styles.badge}>{t(`expense.type.${type}`)}</Badge> : null,
        },
        {
          accessor: 'shares',
          title: '',
          hidden: isMobile,
          render: ({ shares }) => (
            <Avatar.Group>
              {shares.slice(0, 5).map(share => (
                <ShareAvatar key={share.id} share={share} size="sm" />
              ))}
              {shares.length > 5 && <Avatar size={28}>{`+${shares.length - 5}`}</Avatar>}
            </Avatar.Group>
          ),
        },
        {
          accessor: 'amount',
          title: '',
          render: ({ amount }) => (
            <div
              className={classnames(styles.amount, {
                [styles.amountPositive]: amount >= 0,
                [styles.amountNegative]: amount < 0,
              })}
            >
              {formatEuros(amount)}
            </div>
          ),
        },
      ]}
    />
  );
};

export default ExpenseList;
